/**
 * Assietica Gest - Feature Gating Configuration
 * Based on Matrice_Tecnica_Definitiva_v4.md
 * 
 * Piano hierarchy: free < premium < governance < pro < enterprise < convenzioni
 */

// Plan hierarchy for comparison
export const PLAN_HIERARCHY = ['free', 'premium', 'governance', 'pro', 'enterprise', 'convenzioni'];

/**
 * Check if user plan meets minimum required plan
 * @param {string} userPlan - Current user's plan
 * @param {string} requiredPlan - Minimum required plan for the feature
 * @returns {boolean} - True if user can access the feature
 */
export const canAccessFeature = (userPlan, requiredPlan) => {
  if (!requiredPlan || requiredPlan === 'free') return true;
  const userIndex = PLAN_HIERARCHY.indexOf(userPlan || 'free');
  const requiredIndex = PLAN_HIERARCHY.indexOf(requiredPlan);
  return userIndex >= requiredIndex;
};

/**
 * Get the display name for a plan
 */
export const getPlanDisplayName = (plan) => {
  const names = {
    free: 'Free',
    premium: 'Premium',
    governance: 'Governance',
    pro: 'Pro',
    enterprise: 'Enterprise',
    convenzioni: 'Convenzioni'
  };
  return names[plan] || plan;
};

/**
 * Feature definitions with minimum required plan
 * Based on Matrice_Tecnica_Definitiva_v4.md
 */
export const FEATURE_GATES = {
  // ===== AREA 1: GESTIONE ANAGRAFICA PERSONE =====
  anagrafica_base: 'free',
  anagrafica_completa: 'premium',
  anagrafica_avanzata: 'pro',
  campi_personalizzati: 'pro',
  unificazione_duplicati: 'pro',
  
  // ===== AREA 2: GESTIONE VOLONTARI =====
  volontari_base: 'free',
  volontari_completi: 'premium',
  registro_pdf: 'premium',
  registro_pdf_avanzato: 'governance',
  scheda_individuale_pdf: 'governance',
  import_massivo_volontari: 'governance',
  
  // ===== AREA 3: GESTIONE SOCI E ASSOCIATI =====
  soci_base: 'free',
  libro_soci_digitale: 'premium',
  delibera_ammissione: 'governance',
  import_massivo_soci: 'governance',
  
  // ===== AREA 4: QUOTE ASSOCIATIVE =====
  quote_visualizzazione: 'free',  // Solo lettura in Free
  quote_creazione: 'premium',
  quote_sollecito_manuale: 'governance',
  quote_report_incassi: 'governance',
  quote_generazione_massiva: 'pro',
  quote_solleciti_automatici: 'pro',
  
  // ===== AREA 5: ORGANI SOCIALI E GOVERNANCE ⭐ BLOCCATO FREE/PREMIUM =====
  governance_cariche: 'governance',
  governance_assemblee: 'governance',
  governance_scadenze_mandati: 'governance',
  governance_consiglio_direttivo: 'governance',
  governance_scadenzario_obblighi: 'governance',
  governance_poteri_deleghe: 'pro',
  governance_quorum_automatico: 'pro',
  governance_registro_delibere: 'pro',
  governance_calendario: 'pro',
  
  // ===== AREA 6: EVENTI E ATTIVITÀ =====
  eventi_base: 'free',
  eventi_notifiche_email: 'premium',
  calendario_scadenzario: 'premium',  // Calendario unificato scadenze
  eventi_liste_attesa: 'governance',
  eventi_ricorrenti: 'governance',
  eventi_qrcode_checkin: 'pro',
  
  // ===== AREA 7: PRESENZE E ORE =====
  presenze_base: 'free',
  presenze_modifica_manuale: 'premium',
  presenze_report: 'premium',
  presenze_export_csv: 'governance',
  presenze_attestato_pdf: 'governance',
  presenze_trend_grafici: 'pro',
  presenze_import_massivo: 'governance',
  
  // ===== AREA 7b: EXPORT E COMPLIANCE RUNTS =====
  export_runts: 'premium',  // Export CSV per RUNTS
  export_runts_soci: 'premium',
  export_runts_volontari: 'premium',
  export_runts_organi: 'premium',
  
  // ===== AREA 8: DOCUMENTI E ARCHIVIO =====
  documenti_base: 'free',
  documenti_scadenze: 'premium',
  documenti_alert_email: 'governance',
  documenti_audit: 'governance',
  documenti_versioning: 'pro',
  documenti_ricerca_avanzata: 'pro',
  documenti_fulltext_ocr: 'enterprise',
  
  // ===== AREA 9: RENDICONTAZIONE ECONOMICA ⭐ BLOCCATO FREE/PREMIUM =====
  rendicontazione: 'governance',
  rendicontazione_limitata: 'governance',  // 35 categorie
  rendicontazione_completa: 'pro',          // 120+ categorie
  rendicontazione_progetti: 'pro',
  rendicontazione_budget: 'pro',
  rendicontazione_confronti: 'pro',
  rendicontazione_export_commercialista: 'pro',
  rendicontazione_import_massivo: 'governance',
  
  // ===== AREA 9b: SCHEMI MINISTERIALI BILANCIO =====
  bilanci_ministeriali: 'governance',       // MOD. D, C base
  bilanci_ministeriali_mod_d: 'governance', // Rendiconto per Cassa
  bilanci_ministeriali_mod_c: 'governance', // Relazione di Missione
  bilanci_ministeriali_mod_a: 'pro',        // Stato Patrimoniale (> €220k)
  bilanci_ministeriali_mod_b: 'pro',        // Rendiconto Gestionale (> €220k)
  
  // ===== AREA 10: KIT DOCUMENTALE SOCI =====
  kit_soci: 'premium',
  kit_soci_completo: 'governance',
  kit_soci_personalizzabili: 'pro',
  kit_soci_generazione_batch: 'pro',
  kit_soci_firma_digitale: 'enterprise',
  
  // ===== AREA 11: CERTIFICATI E ATTESTATI =====
  certificati: 'premium',
  certificati_personalizzati: 'governance',
  certificati_batch: 'pro',
  certificati_qr_verifica: 'pro',
  certificati_registro: 'pro',
  
  // ===== AREA 12: COMUNICAZIONI =====
  comunicazioni_base: 'free',  // mailto, whatsapp
  comunicazioni_email_singola: 'premium',
  comunicazioni_email_gruppo: 'governance',
  comunicazioni_template: 'governance',
  comunicazioni_storico: 'governance',
  comunicazioni_newsletter: 'pro',
  comunicazioni_automatiche: 'pro',
  comunicazioni_sms: 'enterprise',
  
  // ===== AREA 13: COPERTURE ASSICURATIVE =====
  coperture_base: 'free',
  coperture_scadenze: 'premium',
  coperture_alert_email: 'governance',
  coperture_dashboard_rischi: 'governance',
  coperture_report_pdf: 'pro',
  coperture_integrazione_broker: 'enterprise',
  
  // ===== AREA 14: DASHBOARD E STATISTICHE ⭐ AVANZATE SOLO DA PRO =====
  dashboard_base: 'free',
  dashboard_personalizzazione: 'premium',
  dashboard_grafici_base: 'premium',
  dashboard_alert_scadenze: 'governance',
  dashboard_governance: 'governance',
  dashboard_statistiche_modulo: 'governance',
  statistiche_avanzate: 'pro',
  statistiche_trend: 'pro',
  statistiche_confronti: 'pro',
  statistiche_kpi_personalizzabili: 'pro',
  statistiche_export_pdf: 'pro',
  dashboard_federazione: 'enterprise',
  
  // ===== AREA 15: BACKUP E EXPORT =====
  backup_supporto: 'free',  // Solo su richiesta
  backup_export_csv: 'premium',
  backup_download_zip: 'premium',
  backup_automatici: 'governance',
  backup_export_excel_json: 'pro',
  backup_restore: 'pro',
  backup_api_programmabile: 'enterprise',
  
  // ===== AREA 16: LOG E AUDIT =====
  log_audit: 'premium',
  log_completo: 'governance',
  log_filtri: 'governance',
  log_audit_gdpr: 'governance',
  log_export_compliance: 'pro',
  log_alert_anomalie: 'enterprise',
  
  // ===== AREA 17: UTENTI E ACCESSI =====
  utenti_base: 'free',       // 1 utente
  utenti_multipli: 'premium', // 2 utenti
  utenti_2fa: 'governance',
  utenti_ruoli_base: 'governance',  // 5 utenti
  utenti_ruoli_personalizzati: 'pro',  // 10 utenti
  utenti_sso: 'enterprise',
  
  // ===== AREA 18: SUPPORTO E ASSISTENZA =====
  supporto_standard: 'free',
  supporto_prioritario: 'governance',
  chatbot_ai: 'pro',
  supporto_telefonico: 'enterprise',
  supporto_account_manager: 'enterprise',
  
  // ===== AREA 19: INTEGRAZIONI =====
  integrazione_google_oauth: 'free',  // Nativa
  integrazione_email_brevo: 'premium',
  integrazione_storage_r2: 'premium',
  integrazione_webhook: 'pro',
  integrazione_api_rest: 'enterprise',
  integrazione_zapier: 'enterprise',
  integrazione_erp: 'enterprise',
  
  // ===== AREA 20: FUNZIONALITÀ MASSIVE ⭐ LIMITATE IN GOVERNANCE, COMPLETE DA PRO =====
  massive_certificati_batch: 'governance',  // max 20
  massive_export_pdf_soci: 'governance',     // max 50
  import_massivo: 'governance',
  generazione_massiva_quote: 'pro',
  email_massive: 'pro',
  update_massivo_stati: 'pro',
  
  // ===== AREA 21: MAGAZZINO E AUTOMEZZI ⭐ SOLO DA PRO =====
  magazzino_inventario: 'pro',
  magazzino_assegnazioni: 'pro',
  automezzi: 'pro',
};

/**
 * Get the minimum required plan for a specific feature
 */
export const getRequiredPlan = (feature) => {
  return FEATURE_GATES[feature] || 'free';
};

/**
 * Menu items configuration with feature gates
 * Maps menu paths to their required feature/plan
 */
export const MENU_FEATURE_MAP = {
  // PANORAMICA
  '/dashboard': 'dashboard_base',
  '/notifiche': 'free',
  '/comunicazioni': 'comunicazioni_email_singola',
  '/statistiche': 'free',
  '/diario': 'free',
  
  // VOLONTARI
  '/volontari': 'volontari_base',
  '/volontari-occasionali': 'volontari_base',
  '/volontari-non-occasionali': 'volontari_base',
  '/ore-volontari': 'presenze_report',
  '/presenze': 'presenze_base',
  '/checkin': 'presenze_base',
  
  // SOCI E ASSOCIATI
  '/soci': 'soci_base',
  '/registro-soci': 'libro_soci_digitale',
  '/quote-associative': 'quote_creazione',
  '/kit-soci': 'kit_soci',
  
  // ATTIVITÀ
  '/eventi': 'eventi_base',
  '/scadenzario': 'calendario_scadenzario',
  '/registro': 'registro_pdf',
  '/coperture': 'coperture_base',
  
  // DOCUMENTI
  '/documenti': 'documenti_base',
  '/kit-amministrativo': 'free',
  '/logs': 'log_audit',
  
  // REPORT
  '/reports': 'free',
  '/rendicontazione': 'free',  // Basic view
  '/contabilita': 'rendicontazione',  // Actual accounting - Governance+
  '/libri-sociali': 'governance_organi',  // PDF dei libri sociali - Governance+
  '/export-runts': 'export_runts',  // Export CSV per RUNTS - Premium+
  '/bilanci-ministeriali': 'bilanci_ministeriali', // Schemi Ministeriali - Governance+
  '/statistiche-avanzate': 'statistiche_avanzate',
  
  // STRUMENTI AVANZATI
  '/certificati': 'certificati',
  '/import': 'import_massivo',
  
  // GOVERNANCE (new section)
  '/governance': 'governance_cariche',
  '/assemblee': 'governance_assemblee',
  '/consiglio-direttivo': 'governance_consiglio_direttivo',
  
  // MAGAZZINO E AUTOMEZZI (PRO+)
  '/magazzino': 'magazzino_inventario',
  '/magazzino/assegnazioni': 'magazzino_assegnazioni',
  '/automezzi': 'automezzi',
  '/familiari-assistiti': 'soci_base',
  
  // IMPOSTAZIONI (mostly free)
  '/impostazioni': 'free',
  '/pricing': 'free',
  '/profilo': 'free',
  '/export-dati': 'backup_export_csv',
  '/migrazione-volontari': 'free',
  '/manuale': 'free',
  '/termini': 'free',
  '/privacy': 'free',
  '/cookie-policy': 'free',
};

/**
 * Get the required plan for a menu path
 */
export const getMenuRequiredPlan = (path) => {
  const feature = MENU_FEATURE_MAP[path];
  if (!feature) return 'free';
  return FEATURE_GATES[feature] || 'free';
};

/**
 * Limits per plan based on Matrice v4
 */
export const PLAN_LIMITS = {
  free: {
    max_volontari_attivi: 20,
    max_soggetti_unici: 30,
    max_eventi_anno: 10,
    max_documenti: 10,
    max_storage_mb: 50,
    max_quote_anno: 0,
    max_email_mese: 0,
    max_utenti: 1,
    max_categorie_economiche: 0,
    backup_retention_days: 0,
    log_retention_days: 0,
  },
  premium: {
    max_volontari_attivi: 60,
    max_soggetti_unici: 100,
    max_eventi_anno: 50,
    max_documenti: 100,
    max_storage_mb: 500,
    max_quote_anno: 100,
    max_email_mese: 50,
    max_utenti: 1,
    max_categorie_economiche: 0,
    backup_retention_days: 0,
    log_retention_days: 30,
  },
  governance: {
    max_volontari_attivi: 100,
    max_soggetti_unici: 200,
    max_eventi_anno: 100,
    max_documenti: 300,
    max_storage_mb: 2048,
    max_quote_anno: 300,
    max_email_mese: 200,
    max_utenti: 1,
    max_categorie_economiche: 35,
    backup_retention_days: 30,
    log_retention_days: 90,
    // Massive limits
    massive_certificati_batch_max: 20,
    massive_export_pdf_soci_max: 50,
  },
  pro: {
    max_volontari_attivi: 250,
    max_soggetti_unici: 500,
    max_eventi_anno: -1,  // illimitati
    max_documenti: -1,
    max_storage_mb: 5120,
    max_quote_anno: -1,
    max_email_mese: -1,
    max_utenti: 1,
    max_categorie_economiche: 120,
    backup_retention_days: 90,
    log_retention_days: 365,
  },
  enterprise: {
    max_volontari_attivi: -1,
    max_soggetti_unici: -1,
    max_eventi_anno: -1,
    max_documenti: -1,
    max_storage_mb: 20480,
    max_quote_anno: -1,
    max_email_mese: -1,
    max_utenti: 1,
    max_categorie_economiche: 120,
    backup_retention_days: 365,
    log_retention_days: 1095,  // 3 anni
  },
  convenzioni: {
    max_volontari_attivi: -1,
    max_soggetti_unici: -1,
    max_eventi_anno: -1,
    max_documenti: -1,
    max_storage_mb: 51200,
    max_quote_anno: -1,
    max_email_mese: -1,
    max_utenti: 1,
    max_categorie_economiche: 120,
    backup_retention_days: 365,
    log_retention_days: 1095,
  },
};

export default {
  PLAN_HIERARCHY,
  FEATURE_GATES,
  PLAN_LIMITS,
  canAccessFeature,
  getRequiredPlan,
  getMenuRequiredPlan,
  getPlanDisplayName,
};
